<template>
  <base-dialog
    :show="isOpen"
    :title="$t('common.action.resetPassword')"
    :is-loading="loading"
    :actionName="$t('common.action.reset')"
    :max-width="$vuetify?.display?.mdAndUp ? '35%' : '100%'"
    :hide-action-button="true"
    @close="onClose"
  >
    <template #content>
      <!-- Reset Password Window -->
      <v-window v-model="step">
        <v-window-item :value="1">
          <v-row class="mx-1">
            <v-col class="mb-15">
              <v-row class="justify-center">
                <v-col>
                  <v-card-text class="pl-0 text-body-1 text-grey-darken-2">
                    {{ $t("common.resetPasswordNote") }}
                  </v-card-text>
                  <v-form v-model="isFormValid">
                    <div class="d-flex align-center">
                      <span class="app-field-label">{{ $t("page.signUp.email") }}</span>
                    </div>
                    <v-text-field
                      data-testid="email-input"
                      :rules="emailRules"
                      :placeholder="$t('page.signUp.email')"
                      required
                      :disabled="isFixedEmail"
                      v-model="email"
                    >
                    </v-text-field>
                    <v-btn
                      color="primary"
                      width="100%"
                      :disabled="!isFormValid && !isFixedEmail"
                      size="large"
                      class="text-h6 mt-3 font-weight-regular"
                      @click="resetPassword"
                      submit
                    >
                      {{ $t("common.action.sendInstructions") }}
                    </v-btn>
                  </v-form>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-window-item>
        <!-- Check Email Window -->
        <v-window-item :value="2">
          <check-email-card />
        </v-window-item>
      </v-window>
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { useAuthStore } from "@/auth/stores/auth";
import { useValidation } from "@/shared/composables/validation";
import { computed, ref, watch } from "vue";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";
import CheckEmailCard from "@/auth/components/reset-password/CheckEmailCard.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: null,
  },
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "update:modelValue", value: boolean): void;
}>();

const { emailRules } = useValidation();
const email = ref("");
const internalOpen = ref(false);
const isFormValid = ref(false);
const isFixedEmail = ref(false);
const step = ref(1);
const id = ref("");
const loading = ref(false);

const isControlled = computed(() => props.modelValue !== null);
const isOpen = computed({
  get: () => (isControlled.value ? (props.modelValue as boolean) : internalOpen.value),
  set: (value: boolean) => {
    internalOpen.value = value;
    emit("update:modelValue", value);
  },
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue === null) {
      return;
    }
    internalOpen.value = newValue;
  },
);

// Methods
const resetPassword = () => {
  useAuthStore().resetPassword({
    email: email.value,
  });
  step.value++;
  email.value = "";
};

const onClose = () => {
  isOpen.value = false;
  email.value = "";
  step.value = 1;
  emit("close");
};

const open = (userId: string, userEmail?: string) => {
  email.value = userEmail || "";
  id.value = userId;
  isFixedEmail.value = !!userEmail;
  isOpen.value = true;
};

defineExpose({
  open,
});
</script>
